import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { crearProductoLicencia, editarProductoLicencia, nuevaOperacionId } from '../../services/request/requestLicenciasSoftware';

/**
 * Alta y edición manual de una licencia del stock (spec del backend §5):
 * marca, nombre, contratadas, en uso y precios mensuales. Lo valida el
 * servicio de licencias: permiso gestionarStockLicencias, versión vista y que
 * las contratadas cubran lo usado y lo reservado.
 */

const VACIO = { marca: '', nombre: '', contratadas: '', en_uso: '', precios_clp: '', precios_usd: '', motivo: '' };

function desdeFila(fila) {
  if (!fila) return VACIO;
  const texto = (v) => (v === null || v === undefined ? '' : String(v));
  return {
    marca: texto(fila.marca),
    nombre: texto(fila.licencias),
    contratadas: texto(fila.cant_licencias),
    en_uso: texto(fila.en_uso),
    precios_clp: texto(fila.precios_clp),
    precios_usd: texto(fila.precios_usd),
    motivo: '',
  };
}

/** Lo que se envía: números donde corresponde y precios vacíos como null. */
function cuerpo(valores) {
  const numero = (v) => (v === '' ? null : Number(v));
  return {
    marca: valores.marca.trim(),
    nombre: valores.nombre.trim(),
    contratadas: numero(valores.contratadas) ?? 0,
    en_uso: numero(valores.en_uso) ?? 0,
    precios_clp: numero(valores.precios_clp),
    precios_usd: numero(valores.precios_usd),
    motivo: valores.motivo.trim() || null,
  };
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.fila la fila del resumen que se edita; null para una licencia nueva
 * @param {() => void} props.onClose
 * @param {(resultado: object) => void} props.onGuardado
 */
export default function DialogoLicencia({ open, fila, onClose, onGuardado }) {
  const [valores, setValores] = useState(VACIO);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Un mismo id para los reintentos de un mismo guardado.
  const [operacionId, setOperacionId] = useState(nuevaOperacionId);
  const editar = !!fila;

  useEffect(() => {
    if (open) {
      setValores(desdeFila(fila));
      setError(null);
      setOperacionId(nuevaOperacionId());
    }
  }, [open, fila]);

  const cambiar = (campo) => (e) => setValores({ ...valores, [campo]: e.target.value });
  const entero = (v) => v !== '' && Number.isInteger(Number(v)) && Number(v) >= 0;
  const valido = valores.marca.trim() && valores.nombre.trim() && entero(valores.contratadas || '0') && entero(valores.en_uso || '0');
  const libres = (Number(valores.contratadas) || 0) - (Number(valores.en_uso) || 0) - (fila ? fila.comprometidas || 0 : 0);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const datos = { ...cuerpo(valores), operacion_id: operacionId };
      const resultado = editar
        ? await editarProductoLicencia(fila.producto_id, { ...datos, version: fila.version })
        : await crearProductoLicencia(datos);
      onGuardado(resultado);
    } catch (e) {
      const r = e.response && e.response.data;
      setError((r && r.mensaje) || 'No fue posible guardar la licencia');
      // Si otra persona la cambió, hay que releer: la operación siguiente es otra.
      if (r && r.codigo === 'estado_cambiado') setOperacionId(nuevaOperacionId());
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editar ? `Editar licencia · ${fila.marca} ${fila.licencias}` : 'Nueva licencia'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Marca" value={valores.marca} onChange={cambiar('marca')} fullWidth required variant="outlined" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Licencia" value={valores.nombre} onChange={cambiar('nombre')} fullWidth required variant="outlined" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Total contratadas"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={valores.contratadas}
              onChange={cambiar('contratadas')}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Asignadas (en uso)"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={valores.en_uso}
              onChange={cambiar('en_uso')}
              fullWidth
              variant="outlined"
              helperText="Las que la consola del fabricante cuenta en uso"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Precio mensual CLP" type="number" inputProps={{ min: 0 }} value={valores.precios_clp} onChange={cambiar('precios_clp')} fullWidth variant="outlined" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Precio mensual USD" type="number" inputProps={{ min: 0 }} value={valores.precios_usd} onChange={cambiar('precios_usd')} fullWidth variant="outlined" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Motivo del cambio"
              value={valores.motivo}
              onChange={cambiar('motivo')}
              fullWidth
              variant="outlined"
              helperText="Queda en la bitácora de la licencia, por ejemplo: compra de 5 licencias, OC 1234"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color={libres < 0 ? 'error' : 'textSecondary'}>
              {editar
                ? `Quedarían ${libres} disponibles (contratadas − en uso − ${fila.comprometidas || 0} reservadas o pendientes de instalación).`
                : `Quedarían ${libres} disponibles.`}
            </Typography>
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button onClick={guardar} color="primary" variant="contained" disabled={!valido || guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
